/**
 * Utility functions for PDP Sampling tier configuration
 * Centralizes tier parsing and calculations used across Banner, ProductSamplesModal, and Minidrawer
 */

import Empty from 'constants/empty';
import LanguageLocaleUtils from 'utils/LanguageLocale';

// Default fallback tier when configuration is unavailable
const DEFAULT_TIER = { maxSamples: 3, spendThresholdCents: 2500 };
const DEFAULT_MAX_SAMPLES = DEFAULT_TIER.maxSamples;

/**
 * Parse tier configuration from Sephora global config
 * Handles string parsing with extra quotes and fallback to object
 * Selects country-specific tiers (tiersUS, tiersCA) based on current locale
 * @returns {Array} Parsed tiers array, or `[DEFAULT_TIER]` when config is missing, invalid, or parsing fails
 */
function parseTiersFromConfig() {
    try {
        const config = Sephora?.configurationSettings?.pdpSamplingConfiguration;
        const isCanada = LanguageLocaleUtils.isCanada();

        // Select country-specific tiers
        const tiersData = isCanada ? config?.tiersCA : config?.tiersUS;

        if (!tiersData) {
            return [DEFAULT_TIER];
        }

        const tiersString = typeof tiersData === 'string' ? tiersData : Empty.String;
        const trimmedString = tiersString.trim();
        const cleanedString = trimmedString.startsWith('\'') && trimmedString.endsWith('\'')
            ? trimmedString.slice(1, -1)
            : trimmedString;
        const tiers = typeof tiersData === 'string'
            ? JSON.parse(cleanedString)
            : tiersData;

        if (!Array.isArray(tiers) || tiers.length === 0) {
            return [DEFAULT_TIER];
        }

        const now = new Date();
        const activeTiers = tiers.filter(tier => {
            if (tier.enabled === false) {
                return false;
            }

            if ((tier.startDate && new Date(tier.startDate) > now) || (tier.endDate && new Date(tier.endDate) < now)) {
                return false;
            }

            return true;
        });

        return activeTiers;
    } catch (error) {
        Sephora.logger.error('Error parsing sampling tiers:', error);

        return [DEFAULT_TIER];
    }
}

/**
 * Get the maximum samples allowed (highest tier)
 * @returns {number} Max samples from highest tier, or 3 as fallback
 */
export function getMaxSamples() {
    const tiers = parseTiersFromConfig();

    if (tiers.length === 0) {
        return DEFAULT_MAX_SAMPLES;
    }

    return Math.max(...tiers.map(tier => tier.maxSamples));
}

/**
 * Get all tiers sorted by spend threshold
 * @returns {Array} Sorted tiers array
 */
export function getSortedTiers() {
    const tiers = parseTiersFromConfig();

    if (tiers.length === 0) {
        return [];
    }

    return [...tiers].sort((a, b) => a.spendThresholdCents - b.spendThresholdCents);
}
